import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '@/common/components/Modal';
import RichTextEditor from '@/common/components/RichTextEditor';

interface KanbanCardModalProps {
  card: {
    id: string;
    content: string;
    description: string;
  };
  onClose: () => void;
  onUpdate: (card: { id: string; content: string; description: string }) => Promise<boolean>;
  isOpen: boolean;
}

export const KanbanCardModal = ({ card, onClose, onUpdate, isOpen }: KanbanCardModalProps) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(card.content);
  const [description, setDescription] = useState(card.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    const success = await onUpdate({
      id: card.id,
      content: content.trim(),
      description: description.trim(),
    });

    setIsSaving(false);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg" title={t('kanban.editCard')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <form className="min-w-[600px]" onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.title')}</label>
            <input
              ref={titleInputRef}
              className="w-full p-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD66B]"
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
            <div className="min-h-[300px] max-h-[400px] overflow-auto">
              <RichTextEditor
                content={description}
                placeholder={t('kanban.addDescPlaceholder')}
                onChange={setDescription}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 disabled:opacity-50"
              disabled={isSaving}
              type="button"
              onClick={onClose}
            >
              {t('common.cancel')}
            </button>
            <button
              className="px-4 py-2 text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50"
              disabled={isSaving}
              type="submit"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
